import {
  DayPartSections,
  ID,
  SortingMode,
  Task,
  TaskListTreeItem,
  Timestamp,
  WeekDays,
} from '@griply/types';
import { buildDayPartTree } from '@griply/utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export type Options = {
  tasks: Task[];
  customOrder?: Record<ID, ID[]>;
  sortingMode?: SortingMode | null;
  today: Timestamp;
  weekStartsOn: WeekDays;
  showEmptyGroups?: boolean;
  hideNewTaskRow?: boolean;
};

export const useDayPartTree = ({
  tasks,
  customOrder,
  sortingMode,
  today,
  weekStartsOn,
  showEmptyGroups,
  hideNewTaskRow,
}: Options) => {
  const { t } = useTranslation();

  const dayPartLabels = useMemo<Record<DayPartSections, string>>(
    () => ({
      [DayPartSections.Overdue]: t('general.sections.overdue.title'),
      [DayPartSections.AllDay]: t('general.sections.day-part.allDay.title'),
      [DayPartSections.Morning]: t('general.sections.day-part.morning.title'),
      [DayPartSections.Afternoon]: t(
        'general.sections.day-part.afternoon.title',
      ),
      [DayPartSections.Evening]: t('general.sections.day-part.evening.title'),
    }),
    [t],
  );

  return useMemo<Record<ID, TaskListTreeItem>>(
    () =>
      buildDayPartTree({
        tasks,
        customOrder,
        sortingMode,
        today,
        weekStartsOn,
        showEmptyGroups,
        hideNewTaskRow,
        dayPartLabels,
        rootLabel: 'Root',
        newTaskLabel: 'New Task',
      }),
    [
      customOrder,
      sortingMode,
      tasks,
      today,
      weekStartsOn,
      showEmptyGroups,
      hideNewTaskRow,
      dayPartLabels,
    ],
  );
};
